import $ from "jquery";

const COOKIE_EXPIRY_DAYS = 30;
const CONSENT_VERSION = "2026-06-05-v1";
const COOKIE_POLICY_VERSION = "2026-06-05-v1";

const CONSENT_COOKIES = {
	essential: [
		"user_cookie_consent",
		"user_essential_cookie_consent",
	],
	optional: [
		"user_performance_cookie_consent",
		"user_functional_cookie_consent",
		"user_social_media_cookie_consent",
	],
};

const CONSENT_EVENTS = {
	performance: {
		granted: "on_user_performance_cookie_consent_granted",
		denied: "on_user_performance_cookie_consent_withdrawn",
	},
	functional: {
		granted: "on_user_functional_cookie_consent_granted",
		denied: "on_user_functional_cookie_consent_withdrawn",
	},
	social_media: {
		granted: "on_user_social_media_cookie_consent_granted",
		denied: "on_user_social_media_cookie_consent_withdrawn",
	},
};

const PERFORMANCE_COOKIE_EXACT_NAMES = [
	"_ga",
	"_gid",
	"_gat",
	"_clck",
	"_clsk",
	"CLID",
	"initialTrafficSource",
	"utmzses",
	"utm_source",
	"utm_medium",
	"utm_campaign",
	"utm_term",
	"utm_content",
];

const PERFORMANCE_COOKIE_PREFIXES = [
	"_ga_",
];

const AD_COOKIE_EXACT_NAMES = [
	"_gcl_au",
	"_gcl_aw",
	"_gcl_dc",
	"_gcl_gs",
	"gclid",
	"_fbp",
	"_fbc",
	"_uetsid",
	"_uetvid",
	"_uetmsclkid",
	"MUID",
	"MR",
	"ANONCHK",
	"SM",
];

const AD_COOKIE_PREFIXES = [
	"_gcl_",
];

const UTM_QUERY_PARAMS = [
	"utm_source",
	"utm_term",
	"utm_campaign",
	"utm_medium",
	"utm_content",
];

function setCookie(name, value, days, options = {}) {
	const {
		path = "/",
		domain,
		secure = window.location.protocol === "https:",
		sameSite = "Lax",
	} = options;

	const expires = Number.isFinite(days)
		? `; expires=${new Date(Date.now() + days * 864e5).toUTCString()}`
		: "";
	const domainPart = domain ? `; domain=${domain}` : "";
	const securePart = secure ? "; Secure" : "";
	const encodedValue = encodeURIComponent(String(value));

	document.cookie = `${name}=${encodedValue}${expires}; path=${path}${domainPart}${securePart}; SameSite=${sameSite}`;
}

export function getCookie(name) {
	const cookie = document.cookie
		.split(";")
		.map((part) => part.trim())
		.find((part) => part.startsWith(`${name}=`));

	return cookie ? decodeURIComponent(cookie.substring(name.length + 1)) : "";
}

function getConsentCookieOptions() {
	const host = window.location.hostname.toLowerCase();

	if (host === "localhost" || host === "127.0.0.1") {
		return {};
	}

	if (host === "balisepontoonboats.com" || host.endsWith(".balisepontoonboats.com")) {
		return { domain: ".balisepontoonboats.com" };
	}

	return {};
}

function createConsentCookie(name, value, days = COOKIE_EXPIRY_DAYS) {
	setCookie(name, value, days, getConsentCookieOptions());
}

function getUrlParameter(name) {
	const params = new URLSearchParams(window.location.search);
	return params.has(name) ? decodeURIComponent(params.get(name) || "") : "";
}

function isConsentGranted(cookieName) {
	const value = getCookie(cookieName);
	return value === "true" || value === "1";
}

function getConsentState() {
	return {
		performanceGranted: isConsentGranted("user_performance_cookie_consent"),
		functionalGranted: isConsentGranted("user_functional_cookie_consent"),
		socialGranted: isConsentGranted("user_social_media_cookie_consent"),
	};
}

function saveConsentState(state) {
	CONSENT_COOKIES.essential.forEach((key) => createConsentCookie(key, true));

	createConsentCookie("user_performance_cookie_consent", state.performanceGranted);
	createConsentCookie("user_functional_cookie_consent", state.functionalGranted);
	createConsentCookie("user_social_media_cookie_consent", state.socialGranted);
}

function saveConsentAuditMetadata(source) {
	createConsentCookie("user_cookie_choice_timestamp", new Date().toISOString());
	createConsentCookie("user_cookie_consent_version", CONSENT_VERSION);
	createConsentCookie("user_cookie_policy_version", COOKIE_POLICY_VERSION);
	createConsentCookie("user_cookie_choice_source", source);
}

function deleteCookieEverywhere(name) {
	const host = window.location.hostname.toLowerCase();
	const expiration = "Thu, 01 Jan 1970 00:00:00 GMT";

	const domains = new Set([
		host,
		`.${host.replace(/^www\./, "")}`,
		".balisepontoonboats.com",
	]);

	domains.forEach((domain) => {
		document.cookie = `${name}=; expires=${expiration}; Max-Age=0; path=/; domain=${domain}; Secure; SameSite=Lax`;
		document.cookie = `${name}=; expires=${expiration}; Max-Age=0; path=/; domain=${domain}; Secure; SameSite=None`;
	});

	document.cookie = `${name}=; expires=${expiration}; Max-Age=0; path=/; SameSite=Lax`;
}

function getCurrentCookieNames() {
	return document.cookie
		.split(";")
		.map((cookie) => cookie.trim().split("=")[0])
		.filter(Boolean);
}

function clearCookiesByNameAndPrefix(exactNames, prefixes) {
	const currentCookieNames = getCurrentCookieNames();
	const namesToDelete = new Set();

	exactNames.forEach((name) => {
		if (currentCookieNames.includes(name)) {
			namesToDelete.add(name);
		}
	});

	currentCookieNames.forEach((name) => {
		if (prefixes.some((prefix) => name.startsWith(prefix))) {
			namesToDelete.add(name);
		}
	});

	namesToDelete.forEach(deleteCookieEverywhere);
}

function clearPerformanceCookies() {
	clearCookiesByNameAndPrefix(PERFORMANCE_COOKIE_EXACT_NAMES, PERFORMANCE_COOKIE_PREFIXES);
}

function clearAdvertisingCookies() {
	clearCookiesByNameAndPrefix(AD_COOKIE_EXACT_NAMES, AD_COOKIE_PREFIXES);
}

function updateGoogleConsentMode(state) {
	window.dataLayer = window.dataLayer || [];
	window.gtag = window.gtag || function gtagFallback() {
		window.dataLayer.push(arguments);
	};

	window.gtag("consent", "update", {
		analytics_storage: state.performanceGranted ? "granted" : "denied",
		functionality_storage: state.functionalGranted ? "granted" : "denied",
		personalization_storage: state.functionalGranted ? "granted" : "denied",
		ad_storage: state.socialGranted ? "granted" : "denied",
		ad_user_data: state.socialGranted ? "granted" : "denied",
		ad_personalization: state.socialGranted ? "granted" : "denied",
		security_storage: "granted",
	});
}

function updateMicrosoftUETConsent(state) {
	window.uetq = window.uetq || [];

	window.uetq.push("consent", "update", {
		ad_storage: state.socialGranted ? "granted" : "denied",
	});
}

function updateMetaConsent(state) {
	if (typeof window.__baliseApplyMetaConsent === "function") {
		window.__baliseApplyMetaConsent(state);
		return;
	}

	if (typeof window.fbq === "function") {
		window.fbq("consent", state.socialGranted ? "grant" : "revoke");
	}
}

function updateClarityConsent(state) {
	if (typeof window.__baliseApplyClarityConsent === "function") {
		window.__baliseApplyClarityConsent(state);
		return;
	}

	if (!window.clarity) return;

	window.clarity("consentv2", {
		analytics_Storage: state.performanceGranted ? "granted" : "denied",
		ad_Storage: state.performanceGranted && state.socialGranted ? "granted" : "denied",
	});

	window.clarity("consent", state.performanceGranted);
}

function pushConsentEvent(category, granted) {
	const consentState = granted ? "granted" : "denied";
	const eventName = CONSENT_EVENTS[category][consentState];

	document.dispatchEvent(new Event(eventName));

	window.dataLayer = window.dataLayer || [];
	window.dataLayer.push({
		event: eventName,
		consent_category: category,
		consent_state: consentState,
		consent_version: CONSENT_VERSION,
		cookie_policy_version: COOKIE_POLICY_VERSION,
		consent_timestamp: new Date().toISOString(),
	});
}

function pushConsentSnapshot(state) {
	window.dataLayer = window.dataLayer || [];

	window.dataLayer.push({
		event: "balise_consent_state",
		performanceConsent: state.performanceGranted,
		functionalConsent: state.functionalGranted,
		socialMediaConsent: state.socialGranted,
		consent_version: CONSENT_VERSION,
		cookie_policy_version: COOKIE_POLICY_VERSION,
		consent_timestamp: new Date().toISOString(),
	});
}

function clearCookiesForDeniedConsent(state) {
	if (!state.performanceGranted) {
		clearPerformanceCookies();
	}

	if (!state.socialGranted) {
		clearAdvertisingCookies();
	}
}

function applyConsentState(options = {}) {
	const {
		clearDeniedCookies = true,
		emitCategoryEvents = false,
		pushSnapshot = true,
	} = options;
	const state = getConsentState();

	window.__baliseInitialConsentState = state;

	updateGoogleConsentMode(state);
	updateMicrosoftUETConsent(state);
	updateMetaConsent(state);
	updateClarityConsent(state);

	if (emitCategoryEvents) {
		pushConsentEvent("performance", state.performanceGranted);
		pushConsentEvent("functional", state.functionalGranted);
		pushConsentEvent("social_media", state.socialGranted);
	}

	if (pushSnapshot) {
		pushConsentSnapshot(state);
	}

	if (clearDeniedCookies) {
		clearCookiesForDeniedConsent(state);
	}

	return state;
}

function syncConsentSwitches(state) {
	$("#performanceCookieSwitch").prop("checked", state.performanceGranted);
	$("#functionalCookieSwitch").prop("checked", state.functionalGranted);
	$("#socialmediaCookieSwitch").prop("checked", state.socialGranted);
}

function addAllConsentCookies() {
	const state = {
		performanceGranted: true,
		functionalGranted: true,
		socialGranted: true,
	};

	saveConsentState(state);
	saveConsentAuditMetadata("accept_all");
	syncConsentSwitches(state);
	applyConsentState({ emitCategoryEvents: true, clearDeniedCookies: false });
	loadUTMParameters();
	hideCookieConsent();
}

function addCustomConsentCookies() {
	const state = {
		performanceGranted: $("#performanceCookieSwitch").is(":checked"),
		functionalGranted: $("#functionalCookieSwitch").is(":checked"),
		socialGranted: $("#socialmediaCookieSwitch").is(":checked"),
	};

	saveConsentState(state);
	saveConsentAuditMetadata("custom_preferences");
	syncConsentSwitches(state);
	applyConsentState({ emitCategoryEvents: true, clearDeniedCookies: true });

	if (state.performanceGranted) {
		loadUTMParameters();
	}

	hideCookieConsent();
}

function rejectAllConsentCookies() {
	const state = {
		performanceGranted: false,
		functionalGranted: false,
		socialGranted: false,
	};

	saveConsentState(state);
	saveConsentAuditMetadata("reject_all");
	syncConsentSwitches(state);
	applyConsentState({ emitCategoryEvents: true, clearDeniedCookies: true });
	hideCookieConsent();
}

function isGlobalPrivacyControlEnabled() {
	return navigator.globalPrivacyControl === true || window.globalPrivacyControl === true;
}

function applyGlobalPrivacyControl() {
	if (!isGlobalPrivacyControlEnabled()) return false;

	const previousState = getConsentState();
	const previousSource = getCookie("user_cookie_choice_source");
	const hadOptionalConsent = previousState.performanceGranted || previousState.functionalGranted || previousState.socialGranted;
	const state = {
		performanceGranted: false,
		functionalGranted: false,
		socialGranted: false,
	};

	saveConsentState(state);

	if (hadOptionalConsent || previousSource !== "global_privacy_control") {
		saveConsentAuditMetadata("global_privacy_control");
	}

	syncConsentSwitches(state);
	applyConsentState({ emitCategoryEvents: hadOptionalConsent, clearDeniedCookies: true });
	hideCookieConsent();
	showGPCConfirmation();

	return true;
}

function loadUTMParameters() {
	if (!isConsentGranted("user_performance_cookie_consent")) return;
	if (!window.location.search.includes("utm_")) return;

	UTM_QUERY_PARAMS.forEach((param) => {
		const value = getUrlParameter(param);
		if (value) {
			createConsentCookie(param, value, 1);
		}
	});
}

function showCookieConsent() {
	$("#cookieBox").removeClass("opacity-0 hidden").addClass("show");
}

function hideCookieConsent() {
	$("#cookieBox").removeClass("show").addClass("hidden");
}

function showGPCConfirmation() {
	document.getElementById("gpc-confirmation-cookie-box")?.classList.remove("hidden");
	document.getElementById("gpc-confirmation-cookie-modal")?.classList.remove("hidden");
}

function checkBoxToggle(box) {
	const $box = $(box);
	$box.prop("checked", !$box.prop("checked"));
}

function checkConsentCookies() {
	if (isGlobalPrivacyControlEnabled()) {
		hideCookieConsent();
		showGPCConfirmation();
		return;
	}

	if (isConsentGranted("user_cookie_consent")) {
		hideCookieConsent();
	} else {
		showCookieConsent();
	}
}

function scrollToCcpaSection() {
	if (!window.location.href.includes("CCPA")) return;

	setTimeout(() => {
		document.getElementById("CCPA")?.scrollIntoView();
	}, 2000);
}

function bindCookieConsentEvents() {
	$(document).off(".baliseCookieConsent");

	$(document).on("click.baliseCookieConsent", "#show-cookie-consent", showCookieConsent);

	$(document).on(
		"click.baliseCookieConsent",
		"#cookie-modal-all-accept, #cookie-banner-close-btn-US, #cookie-accept-btn-intl",
		addAllConsentCookies
	);

	$(document).on("click.baliseCookieConsent", "#cookie-modal-custom-accept", addCustomConsentCookies);

	$(document).on(
		"click.baliseCookieConsent",
		"#cookie-modal-reject-all, #cookie-reject-all-btn, #cookie-reject-all-btn-intl",
		rejectAllConsentCookies
	);

	$(document).on("keydown.baliseCookieConsent", ".cookie-settings-switch", function onCookieSwitchKeydown(event) {
		if (event.key === "Enter" || event.keyCode === 13) {
			checkBoxToggle(this);
			event.preventDefault();
		}

		event.stopPropagation();
	});
}

$(function initCookieConsent() {
	bindCookieConsentEvents();
	syncConsentSwitches(getConsentState());

	if (!applyGlobalPrivacyControl()) {
		applyConsentState({ clearDeniedCookies: false, emitCategoryEvents: false });
		loadUTMParameters();
		checkConsentCookies();
	}

	scrollToCcpaSection();
});

window.loadUTMParameters = loadUTMParameters;
window.getCookie = getCookie;
window.createConsentCookie = createConsentCookie;
window.applyBaliseConsentState = applyConsentState;
